/* hs-eslint ignored failing-rules */

/* eslint-disable react/no-unused-prop-types */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { memo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { EERIE } from 'HubStyleTokens/colors';
import Small from 'UIComponents/elements/Small';
import UITruncateString from 'UIComponents/text/UITruncateString';
import { ColumnValues, ColumnValuesToKey, HtmlEscapeCodes, EntityTypes, TextColumns, ALL_NETWORKS, ColumnValuesToContactParams, Networks } from 'AdsUI/lib/Constants';
import GlobalFiltersRecord from 'AdsUI/records/reporting/GlobalFiltersRecord';
import AdAccountRecord from 'AdsUI/records/ad-accounts/AdAccountRecord';
import ReportingCampaignRecord from 'AdsUI/records/reporting/ReportingCampaignRecord';
import { extractQualityScoreWithDefault } from 'AdsUI/lib/AdWordsQuality';
import MaxCPC from 'AdsUI/components/campaign-details/MaxCPC';
import ContactListDropContainer from 'AdsUI/components/ContactListDropContainer';
import ROIValue from 'AdsUI/components/ROIValue';
import DealListDrop from 'AdsUI/components/DealListDrop';
import I18n from 'I18n';
import TrackingStatus from 'AdsUI/components/campaign-reporting/TrackingStatus';
import { formatPercentage } from 'AdsUI/lib/Calculations';
import { getCurrencySymbol, getTypeForEntity } from 'AdsUI/lib/Utils';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import { isLeadGen } from 'AdsUI/lib/trackingStatus';
import ReportRecord from 'AdsUI/records/reporting/ReportRecord';
import EntityPropType from 'AdsUI/records/reporting/EntityPropType';
import SingleCurrencyMetric from '../SingleCurrencyMetric';
import MultiCurrencyMetric from '../MultiCurrencyMetric';
import CostPerMetric from '../CostPerMetric';
export const renderDecimalAsPercentage = decimal => typeof decimal === 'number' ? formatPercentage(decimal * 100) : '';

const extractValue = (entity, report, path) => {
  if (!path) {
    return null;
  }

  const [source, key] = path;

  if (source === 'entity') {
    return entity && entity[key];
  }

  return report && report[key];
};

const formatContents = ({
  column,
  report,
  entity,
  trackingStatus,
  entityType,
  parent,
  adAccount,
  campaignEntity,
  globalFilters,
  disableTrackingStatus = false,
  hasControlButtons,
  columnDisplayLabels
}) => {
  const {
    timeRange,
    attributionView
  } = globalFilters;
  const network = adAccount ? adAccount.network : ALL_NETWORKS;
  const adGroupEntity = entityType === EntityTypes.AD || entityType === EntityTypes.KEYWORD ? parent : entityType === EntityTypes.ADGROUP ? entity : null;
  const leadGen = isLeadGen(campaignEntity, adGroupEntity);
  const isTrackingEnabled = leadGen || (adAccount ? adAccount.trackingEnabled : true);
  const cpcCurrencySymbol = adAccount && getCurrencySymbol(adAccount.currencyCode);

  if (report && ColumnValuesToKey[column]) {
    const value = extractValue(entity, report, ColumnValuesToKey[column]);

    if (value === undefined) {
      // Certain columns only exist on certain entities. Most likely case here is a total column
      // that's using something that doesn't exist on the parent. In any case, if the column doesn't
      // exist, we can't display anything, so just bail out.
      return HtmlEscapeCodes.DASH;
    }

    switch (column) {
      case ColumnValues.AD_ACCOUNT:
        {
          return adAccount && adAccount.getDisplayName();
        }

      case ColumnValues.TYPE:
        {
          return getTypeForEntity(entity, entityType, campaignEntity);
        }

      case ColumnValues.CLICK_THROUGH_RATE:
      case ColumnValues.CONVERSION_RATE:
        return renderDecimalAsPercentage(value);

      case ColumnValues.COST:
        return /*#__PURE__*/_jsx(SingleCurrencyMetric, {
          column: columnDisplayLabels[column],
          replacementColumn: columnDisplayLabels[ColumnValues.CONVERTED_COST],
          value: value
        });

      case ColumnValues.FIRST_PAGE_CPC:
      case ColumnValues.TOP_CPC:
        return value !== null ? /*#__PURE__*/_jsx(FormattedCurrency, {
          value: value,
          unit: cpcCurrencySymbol,
          simplifyCurrency: true
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "notApplicableAbbreviation"
        });

      case ColumnValues.CONVERSION_PRICE:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.newContacts
          });
        }

      case ColumnValues.CPC:
      case ColumnValues.CONVERTED_CPC:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.clicks
          });
        }

      case ColumnValues.COST_PER_DEAL:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.deals
          });
        }

      case ColumnValues.COST_PER_LEAD:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.leads
          });
        }

      case ColumnValues.COST_PER_MQL:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.marketingQualifiedLeads
          });
        }

      case ColumnValues.COST_PER_OPPORTUNITY:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.opportunities
          });
        }

      case ColumnValues.COST_PER_SESSION:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.sessions
          });
        }

      case ColumnValues.COST_PER_SQL:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.salesQualifiedLeads
          });
        }

      case ColumnValues.COST_PER_CUSTOMER:
        {
          return /*#__PURE__*/_jsx(CostPerMetric, {
            column: columnDisplayLabels[column],
            value: value,
            inconvertibleCurrencies: report.inconvertibleCurrencies,
            divisor: report.customers
          });
        }

      case ColumnValues.CONVERTED_COST:
      case ColumnValues.CONVERTED_DEALS_REVENUE:
        return /*#__PURE__*/_jsx(MultiCurrencyMetric, {
          column: columnDisplayLabels[column],
          value: value,
          inconvertibleCurrencies: report.inconvertibleCurrencies
        });

      case ColumnValues.DEALS_REVENUE:
        return /*#__PURE__*/_jsx(SingleCurrencyMetric, {
          column: columnDisplayLabels[column],
          replacementColumn: columnDisplayLabels[ColumnValues.CONVERTED_DEALS_REVENUE],
          value: value
        });

      case ColumnValues.CONVERSIONS:
        {
          const conversionsContent = /*#__PURE__*/_jsx(ContactListDropContainer, {
            entity: entity,
            entityType: entityType,
            network: network,
            value: value,
            funnelStage: ColumnValuesToContactParams[column],
            globalFilters: globalFilters
          });

          return hasControlButtons ? conversionsContent : /*#__PURE__*/_jsx(TrackingStatus, {
            trackingStatus: trackingStatus,
            isTrackingEnabled: isTrackingEnabled,
            adAccount: adAccount,
            entityType: entityType,
            entity: entity,
            network: network,
            disabled: disableTrackingStatus,
            isLeadGen: leadGen,
            timeRange: timeRange,
            children: conversionsContent
          });
        }

      case ColumnValues.LEADS:
      case ColumnValues.MARKETING_QUALIFIED_LEADS:
      case ColumnValues.SALES_QUALIFIED_LEADS:
      case ColumnValues.OPPORTUNITIES:
      case ColumnValues.CUSTOMERS:
        return /*#__PURE__*/_jsx(ContactListDropContainer, {
          entity: entity,
          entityType: entityType,
          network: network,
          timeRange: timeRange,
          attributionView: attributionView,
          value: value,
          column: column,
          funnelStage: ColumnValuesToContactParams[column],
          globalFilters: globalFilters
        });

      case ColumnValues.DEALS:
        return /*#__PURE__*/_jsx(DealListDrop, {
          deals: report.dealsDetails,
          total: value
        });

      case ColumnValues.ROI:
        return /*#__PURE__*/_jsx(ROIValue, {
          data: report,
          dontShowCalculate: true
        });

      case ColumnValues.MAX_CPC:
        return /*#__PURE__*/_jsx(MaxCPC, {
          value: value,
          entity: entity,
          entityType: entityType,
          currencySymbol: cpcCurrencySymbol
        });

      case ColumnValues.QUALITY:
        {
          const qualityScore = extractQualityScoreWithDefault(value);
          return /*#__PURE__*/_jsx("span", {
            className: 'quality-score' + (qualityScore > 3 ? " success" : ""),
            children: `${qualityScore}/10`
          });
        }

      case ColumnValues.ENGAGEMENTS:
      case ColumnValues.LIKES:
        if (value === null) {
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "notApplicableAbbreviation"
          });
        }

        return I18n.formatNumber(value);

      case ColumnValues.KEY_RESULTS:
        {
          if (!campaignEntity && !entity || value === null || network === Networks.LINKEDIN && entityType === EntityTypes.CAMPAIGN) {
            return HtmlEscapeCodes.DASH;
          }

          const keyResultEntity = network !== Networks.LINKEDIN ? campaignEntity || entity : entityType === EntityTypes.ADGROUP ? entity : parent;
          const {
            keyResultSource
          } = keyResultEntity;
          return /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(FormattedNumber, {
              value: value
            }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Small, {
              style: {
                color: EERIE
              },
              children: keyResultSource && keyResultSource !== 'UNKNOWN' ? /*#__PURE__*/_jsx(UITruncateString, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `campaigns.keyResultsSource.${keyResultSource}`
                })
              }) : HtmlEscapeCodes.DASH
            })]
          });
        }

      default:
        return I18n.formatNumber(value);
    }
  }

  return null;
};

const ReportingDataCell = /*#__PURE__*/memo(props => /*#__PURE__*/_jsx("td", {
  className: `${TextColumns.indexOf(props.column) === -1 ? 'number-column' : ''} ${props.column.toLowerCase()} campaign-reporting-data-cell`,
  children: formatContents(props)
}));
ReportingDataCell.displayName = 'CampaignReportingDataCell';
ReportingDataCell.propTypes = {
  column: PropTypes.string.isRequired,
  entity: EntityPropType,
  report: PropTypes.instanceOf(ReportRecord).isRequired,
  entityType: PropTypes.oneOf(Object.values(EntityTypes)),
  disableTrackingStatus: PropTypes.bool,
  adAccount: PropTypes.instanceOf(AdAccountRecord),
  campaignEntity: PropTypes.instanceOf(ReportingCampaignRecord),
  globalFilters: PropTypes.instanceOf(GlobalFiltersRecord).isRequired,
  columnDisplayLabels: PropTypes.object.isRequired
};
export default ReportingDataCell;